import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  ShieldCheckIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentTextIcon,
  TagIcon,
  FolderOpenIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  XMarkIcon,
  PencilSquareIcon,
  TrashIcon,
  PlusIcon,
  FlagIcon,
  CloudArrowUpIcon,
} from '@heroicons/react/24/outline';

import { toast } from '../../utils/notify';
import {
  getDocumentLibraryMasterApi,
  uploadDocumentLibraryMasterFileApi,
  getDocumentLibraryMasterFileUrlApi,
  getDocumentLibraryMasterFileBlobApi,
  createDocumentLibraryMasterApi,
  updateDocumentLibraryMasterApi,
  deleteDocumentLibraryMasterApi,
  getCustomerFrameworkDocumentApi,
  getCustomerDocumentFileUrlApi,
  getCustomerDocumentFileApi,
  acknowledgeDocumentApi,
  acknowledgeControlsApi,
  exportDocumentLibraryMasterApi,
  getUserControlsApi,
  approveDocumentApi,
  flagDocumentApi,
  addAuditorCommentApi,
  uploadFlaggedDocumentFileApi,
  addCommentToFlaggedApi,
  getFlaggedFileUrlApi,
  getCustomerFrameworkListApi,
  getCustomerFrameworkControlsApi,
  createCustomerDocumentApi,
  updateCustomerDocumentApi,
  uploadCustomerDocumentFileApi,
} from '../../services/axios/AuthApis/TrustCenterApis';
import { useSnackbar } from '../../context/SnackbarContext';
import ApiLoader from '../ui/ApiLoader';
import { DataTable } from '../common/DataTable';
import UploadFileModal from '../ui/UploadFileModal';
import DocFormModal from './DocFormModal';
import DocumentPreviewModal from './DocumentPreviewModal';
import { useSelector } from 'react-redux';
import { useAuth } from '../../context/AuthContext';
import { getAcceptedByAuditorApi, getFlaggedByAuditorApi, getNotApplicableApi, getNotReadyForAuditApi, getReadyForAuditApi } from '../../services/axios/AuthApis/DocumentLibraryApis';
import CustomizedFileApprovalView from './CustomizedFileApprovalView';
/* ── Delete Confirmation Modal ── */
const DeleteConfirmModal = ({ record, onClose, onConfirm }) => {
  const [deleting, setDeleting] = useState(false);
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-red-100 rounded-full"><TrashIcon className="w-5 h-5 text-red-600" /></div>
            <h3 className="font-bold text-gray-900 text-lg">Delete Document</h3>
          </div>
          <p className="text-sm text-gray-600">
            Are you sure you want to delete <span className="font-semibold text-gray-900">"{record?.policy_procedure_guideline}"</span>? This action cannot be undone.
          </p>
        </div>
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
          <button
            disabled={deleting}
            onClick={async () => {
              setDeleting(true);
              try { await onConfirm(); } finally { setDeleting(false); }
            }}
            className="px-5 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {deleting && <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const PAGE_SIZE = 20;

const DocumentLibraryMasterView = ({ frameworks, userRole, customer_id, selectedFramework, setSelectedFramework, openDocumentId, onDocumentOpened }) => {
  const { showSnackbar } = useSnackbar();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, page_size: 20, total_pages: 1, count: 0,readyForAudit:0,flagged:0,accepted:0,notApplicable:0,notReadyForAudit:0 });
  const [pageSize, setPageSize] = useState(20);

  const [search, setSearch] = useState('');
  const [frameworkFilter, setFrameworkFilter] = useState(selectedFramework ? selectedFramework.id : '');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [controlDomainFilter, setControlDomainFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [reviewFrequencyFilter, setReviewFrequencyFilter] = useState('');
  const [uploadTarget, setUploadTarget] = useState(null); // { id, label }
  const [selectedFile, setSelectedFile] = useState(null);
  const [viewerItem, setViewerItem] = useState(null);    // { fileUrl, label }
  const [viewerLoading, setViewerLoading] = useState(null); // rec.id while fetching presigned URL
  const [formModal, setFormModal] = useState(null);       // null | {} (create) | record (edit)
  const [deleteTarget, setDeleteTarget] = useState(null); // record to delete
  const [exportLoading, setExportLoading] = useState(false); // for export button

  // Derive unique categories from loaded records for the filter dropdown
  const [allCategories, setAllCategories] = useState([]);
  const [showDocumentPreviewModal, setShowDocumentPreviewModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const[selectedDocControl,setSelectedDocControl] = useState(null);
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [selectedDocControls, setSelectedDocControls] = useState([]);
  const [selectedDocShowToEmployees, setSelectedDocShowToEmployees] = useState(false);
  const [showControlsAckModal, setShowControlsAckModal] = useState(false);
  const [checkedControlIds, setCheckedControlIds] = useState([]);
  const [ackLoading, setAckLoading] = useState(false);
  const [documentPreviewTitle, setDocumentPreviewTitle] = useState('');
  const [documentPreviewLoading, setDocumentPreviewLoading] = useState(false);
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState('');
  // A Word file is rendered in the app (docx-preview) from its bytes, not from a URL.
  const [documentPreviewDocx, setDocumentPreviewDocx] = useState(null);
  const [documentPreviewFile, setDocumentPreviewFile] = useState(null); // downloaded PDF/image/text
  const previewObjectUrlRef = useRef(null);
  const [isPreviewScrollCompleted, setIsPreviewScrollCompleted] = useState(false);
  const [previewDocumentMeta, setPreviewDocumentMeta] = useState(null);
  const [previewPageCount, setPreviewPageCount] = useState(1);
  const [previewCurrentPage, setPreviewCurrentPage] = useState(1);
  const previewScrollContainerRef = React.useRef(null);
  const [selectedSatsTab, setSelectedStatsTab] = useState('Total Documents');
  const [openAuditorAction, setOpenAuditorAction] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedRowControlId,setExpandedRowControlId] = useState(null);
  // Rows whose Acknowledged summary ("3 / 13") is expanded to the per-control list.
  const [expandedAckRows, setExpandedAckRows] = useState({});
  const [openEndUserAction, setOpenEndUserAction] = useState(false);
  const fetchRequestIdRef = useRef(0);
  const { user, token } = useAuth();
  const [auditorComment, setAuditorComment] = useState('');
  const [auditorActionLoading, setAuditorActionLoading] = useState(null); 
  const [auditorActionType, setAuditorActionType] = useState(null); 
  const [auditorComments, setAuditorComments] = useState([]);
  const [uploadingFileFor, setUploadingFileFor] = useState(null); // { flaggedId, controlId, recordId }
  const [savingCommentFor, setSavingCommentFor] = useState(null);
  const [newCommentText, setNewCommentText] = useState('');
  const [showUploadFile,setShowUploadFile]=useState(null); 
  const [showUploadFlagFile,setShowUploadFlagFile] = useState(false)
  const [uploadedFlaggedFile, setUploadedFlaggedFile] = useState(false);
  const [selectedFlaggedId,setSelectedFlaggedId] = useState(null)
  const handleUploadFlaggedFile = async (file) => {
    const payload = {
        "flaggedId":selectedFlaggedId,
        "documentFile":uploadedFlaggedFile
    }
    const res = await uploadFlaggedDocumentFileApi(payload)
    console.log({res});
    toast.success("File attached successfully")
  };
  const handlePreviewScroll = (event) => {
    const element = event.currentTarget;
    const reachedBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 8;
    const isLastPage = (previewCurrentPage || 1) >= (previewPageCount || 1);
    setIsPreviewScrollCompleted(reachedBottom && isLastPage);
  };
  const handleUserComment = async(rec)=>{
    if (!newCommentText.trim()) {
      showSnackbar('Please enter a comment.', 'error');
      return;
    }    
    console.log({rec})
    setSavingCommentFor(rec.id);
    try {
      const payload = {
        "flaggedId":rec.flagged_id,
        "comment":newCommentText.trim()
      }
      await addCommentToFlaggedApi(payload);
      showSnackbar('Comment added.', 'success');
      setNewCommentText('');
      fetchData(currentPage); // refresh to show new comment
    } catch (err) {
      showSnackbar('Failed to add comment.', 'error');
    } finally {
      setSavingCommentFor(null);
    }
  
  }
  const handleAgreeDocument = async () => {
    // Auditor never acknowledges - guard in case the modal is opened for one.
    if (userRole === 'auditor') return;
    try {
      const res = await acknowledgeDocumentApi(selectedDocument, selectedDocControl);
      if (res.success) {
        setIsAcknowledged(true);
        setShowDocumentPreviewModal(false);
      } else {
        setShowDocumentPreviewModal(false);
        toast.error(res.message || "Failed to acknowledge document");
        return;
      }
      toast.success("Document acknowledged");
    } catch (error) {
      console.error("Error acknowledging document:", error);
      toast.error("Failed to update acknowledgement");
    }
  };

  const handleConfirmControlsAck = async (selectedControlIds) => {
    // Auditor never acknowledges - guard in case the modal is opened for one.
    if (userRole === 'auditor') return;
    try {
      const res = await acknowledgeControlsApi(selectedDocument, selectedControlIds);
      if (res.success) {
        toast.success(`${selectedControlIds.length} control${selectedControlIds.length !== 1 ? "s" : ""} acknowledged`);
        setShowDocumentPreviewModal(false);
        fetchData(currentPage);
      } else {
        toast.error(res.message || "Failed to acknowledge controls");
      }
    } catch (error) {
      console.error("Error acknowledging controls:", error);
      toast.error("Failed to acknowledge controls");
    }
  };

  // Customer Admin's own document/control picker for DocFormModal - reuses the
  // same modal UI as Platform Admin, but sourced from the caller's own
  // customer_framework/customer_controls data (never the global master
  // catalog), so it can only ever browse/link controls that belong to their
  // own customer.
  const loadCustomerAdminFrameworksForDocForm = useCallback(async () => {
    if (!customer_id) return [];
    const res = await getCustomerFrameworkListApi(customer_id);
    // get_customer_framework_api wraps the list under "framework" (singular) -
    // { success, len, framework: [...] } - not "frameworks"/"results". It
    // returns EVERY framework_master record with an `enabled` flag computed
    // per-customer, so the actual "assigned to this customer" filter has to
    // happen here (enabled + a real customer_framework_id).
    const list = Array.isArray(res) ? res : (res?.framework || res?.results || res?.frameworks || []);
    return list
      .filter((fw) => fw.enabled && fw.customer_framework_id)
      .map((fw) => ({ id: fw.customer_framework_id, name: fw.name }));
  }, [customer_id]);

  const loadCustomerAdminControlsForDocForm = useCallback(async (customerFrameworkId) => {
    if (!customerFrameworkId) return [];
    const data = await getCustomerFrameworkControlsApi(customerFrameworkId, 1, 500, '');
    return data.controls || data.results || [];
  }, []);
  const fetchData = useCallback(async (page = 1, pageSizeArg = pageSize) => {
    const requestId = ++fetchRequestIdRef.current;
    setLoading(true);
    try {
      let data;
      if (userRole === 'platform_admin' || userRole === 'admin') {
        data = await getDocumentLibraryMasterApi(page, pageSizeArg, {
          search,
          framework_id: frameworkFilter,
          category: categoryFilter,
        });
      } else if (userRole === 'end_user') {
        data = await getUserControlsApi({
          page,
          pageSize: pageSizeArg,
          search,
          frameworkId: frameworkFilter,
          category: categoryFilter,
        });
      } else {
        data = await getCustomerFrameworkDocumentApi(page, pageSizeArg, {
          search,
          framework_id: frameworkFilter,
          category: categoryFilter,
          customer_id: customer_id
        });
      }
      if (requestId !== fetchRequestIdRef.current) {
        return;
      }
      setRecords(data.results || []);
      setPagination({
        page: data.page || 1,
        page_size: pageSizeArg,
        total_pages: data.total_pages || 1,
        count: data.count || 0,
        readyForAudit: data.ready_for_auditor || 0,
        flagged: data.flagged_by_auditor || 0,
        accepted: data.accepted_by_auditor || 0,
        notApplicable: data.not_applicable || 0,
        notReadyForAudit:data.not_ready_to_audit || 0
      });
      // Collect categories from this page to populate filter (only on first unfiltered load)
      if (!search && !frameworkFilter && !categoryFilter && page === 1) {
        const cats = [...new Set((data.results || []).map((r) => r.category).filter(Boolean))];
        setAllCategories((prev) => {
          const merged = [...new Set([...prev, ...cats])];
          return merged.sort();
        });
      }
    } catch (err) {
      showSnackbar(err?.response?.data?.error || 'Failed to load records.', 'error');
      setRecords([]);
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [search, frameworkFilter, categoryFilter, userRole, customer_id, showSnackbar]);

  // Fetch on filter change — reset to page 1
  useEffect(() => {
    fetchData(1, pageSize);
  }, [fetchData, pageSize]);

  // Auto-open document preview when navigated from another page
  useEffect(() => {
    if (!openDocumentId || loading || records.length === 0) return;
    const rec = records.find(r => (r.id || r._id) === openDocumentId);
    if (rec) {
      handleViewFile(rec);
      if (onDocumentOpened) onDocumentOpened();
    }
  }, [openDocumentId, records, loading]);

  const handlePageChange = (newPage) => {
    fetchData(newPage, pageSize);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPagination((prev) => ({ ...prev, page: 1, page_size: newPageSize }));
  };

  const handlePaginationChange = (newPage, newPageSize) => {
    if (newPageSize !== pageSize) {
      handlePageSizeChange(newPageSize);
    } else {
      handlePageChange(newPage);
    }
  };

  const { page: currentPage, total_pages: totalPages, count: totalCount, readyForAudit, flagged, accepted, notApplicable, notReadyForAudit} = pagination;
  let stats = userRole == "auditor"?[
    {
      label: 'Total Documents',
      value: totalCount,
      icon: DocumentTextIcon,
      color: 'text-blue-600 bg-blue-50',
    },
    {
      label: 'Not Ready for audit',
      value: notReadyForAudit,
      icon: DocumentTextIcon,
      color: 'text-blue-600 bg-blue-50',
    },{
      label: 'Ready for audit',
      value: readyForAudit,
      icon: DocumentTextIcon,
      color: 'text-blue-600 bg-blue-50',
    },
    {
      label: 'Flagged by auditor',
      value: flagged,
      icon: ShieldCheckIcon,
      color: 'text-purple-600 bg-purple-50',
    },
    {
      label: 'Accepted',
      value: accepted,
      icon: FolderOpenIcon,
      color: 'text-orange-600 bg-orange-50',
    },
    {
      label: 'Not applicable',
      value: notApplicable,
      icon: TagIcon,
      color: 'text-green-600 bg-green-50',
    },
  ]: [{
      label: 'Total Documents',
      value: totalCount,
      icon: DocumentTextIcon,
      color: 'text-blue-600 bg-blue-50',
    },
    {
      label: 'Frameworks',
      value: (frameworks || []).length,
      icon: ShieldCheckIcon,
      color: 'text-purple-600 bg-purple-50',
    },
    {
      label: 'Categories',
      value: allCategories.length,
      icon: FolderOpenIcon,
      color: 'text-orange-600 bg-orange-50',
    },
    {
      label: 'Showing',
      value: records.length,
      icon: TagIcon,
      color: 'text-green-600 bg-green-50',
    },
  ];

  const handleStatClick = async(label) => {
    if (userRole !== "auditor") return;
    let res;
    try{
      setSelectedStatsTab(label);
      setRecords([]);
      setLoading(true);
      switch(label) {
        case "Total Documents":
          fetchData(currentPage);
            break;
        case "Not Ready for audit":
          res = await getNotReadyForAuditApi(1, PAGE_SIZE, {
              search,
              framework_id: frameworkFilter,
              category: categoryFilter,
              customer_id: customer_id
            });
          break;
        case "Ready for audit":
          res = await getReadyForAuditApi(1, PAGE_SIZE, {
              search,
              framework_id: frameworkFilter,
              category: categoryFilter,
              customer_id: customer_id
            });
          break;
        case "Flagged by auditor":
          res = await getFlaggedByAuditorApi(1, PAGE_SIZE, {
              search,
              framework_id: frameworkFilter,
              category: categoryFilter,
              customer_id: customer_id
            });
          break;
        case "Accepted":
          res = await getAcceptedByAuditorApi(1, PAGE_SIZE, {
              search,
              framework_id: frameworkFilter,
              category: categoryFilter,
              customer_id: customer_id
            });
          break;
        case "Not applicable":
          res = await getNotApplicableApi(1, PAGE_SIZE, {
              search,
              framework_id: frameworkFilter,
              category: categoryFilter,
              customer_id: customer_id
            });
          break;
        default:
          break;
      }
      if (res) {
        setRecords(res.results || []);
        setPagination({
          page: res.page || 1,
          total_pages: res.total_pages || 1,
          count: res.count || 0,
          readyForAudit: res.ready_for_auditor || 0,
          flagged: res.flagged_by_auditor || 0,
          accepted: res.accepted_by_auditor || 0,
          notApplicable: res.not_applicable || 0,
          notReadyForAudit:res.not_ready_to_audit || 0
        });
      }
    }catch (err) {
      console.error('Error fetching stat data:', err);
    }finally {
        setLoading(false);
    }
};

  const getFileExtension = (url = "") => {
    const cleanUrl = String(url).split("?")[0].split("#")[0];
    const parts = cleanUrl.split(".");
    return parts.length > 1 ? String(parts.pop()).toLowerCase() : "";
  };

  const DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

  const getOfficeEmbedUrl = (url = "") => {
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
  };
  const prepareDocumentPreview = async (fileUrl) => {
    const extension = getFileExtension(fileUrl);

    setDocumentPreviewLoading(true);
    setDocumentPreviewUrl("");
    setPreviewPageCount(1);
    setPreviewCurrentPage(1);

    try {
      if (extension === "doc" || extension === "docx") {
        setDocumentPreviewUrl(getOfficeEmbedUrl(fileUrl));
        setPreviewPageCount(1);
        setPreviewCurrentPage(1);
        return;
      }

      setDocumentPreviewUrl(fileUrl);
      setPreviewPageCount(1);
      setPreviewCurrentPage(1);
    } finally {
      setDocumentPreviewLoading(false);
    }
  };

  const releasePreviewObjectUrl = () => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
  };

  // Show a file fetched through our backend. Same-origin content lets the viewer
  // track scrolling, so "I Agree" enables itself once the reader reaches the end.
  // Returns false for types the app cannot render (old .doc, spreadsheets ...).
  const prepareDocumentPreviewFromBlob = (blob) => {
    const type = (blob && blob.type) || '';
    releasePreviewObjectUrl();
    setPreviewPageCount(1);
    setPreviewCurrentPage(1);
    if (type === DOCX_MIME_TYPE) {
      setDocumentPreviewUrl('');
      setDocumentPreviewFile(null);
      setDocumentPreviewDocx(blob);
      return true;
    }
    // Everything else is shown from memory (PDF, image, text) or offered as a
    // download by the viewer: S3 and blob: links are blocked by the server's
    // Content-Security-Policy.
    setDocumentPreviewDocx(null);
    setDocumentPreviewUrl('');
    setDocumentPreviewFile(blob);
    return true;
  };

  useEffect(() => {
    if (!showDocumentPreviewModal) {
      releasePreviewObjectUrl();
      setDocumentPreviewDocx(null);
      setDocumentPreviewFile(null);
    }
  }, [showDocumentPreviewModal]);

  useEffect(() => releasePreviewObjectUrl, []);

  useEffect(() => {
    if (!showDocumentPreviewModal) {
      return;
    }
    setIsPreviewScrollCompleted(false);
    if (previewScrollContainerRef.current) {
      previewScrollContainerRef.current.scrollTop = 0;
    }
  }, [showDocumentPreviewModal, documentPreviewUrl]);
  const handleViewFile = async (rec) => {
    const recordId = rec.id || rec._id;
    setViewerLoading(recordId);
    setIsPreviewScrollCompleted(false);
    setSelectedDocument(recordId);
    setSelectedDocControl(rec.control_id);
    const recControls = Array.isArray(rec.controls) ? rec.controls : (rec.controls ? [rec.controls] : []);
    setSelectedDocControls(recControls);
    setSelectedDocShowToEmployees(!!rec.show_to_employees);
    // For customer roles: hide I Agree only when ALL applicable controls are already acknowledged
    const allControlsAcked =
      recControls.length > 0 &&
      recControls.filter(c => c.is_applicable !== false).every(c => c.is_acknowledged);
    setIsAcknowledged(
      (userRole === 'customer_admin' || userRole === 'security_analyst')
        ? allControlsAcked
        : !!rec.is_acknowledged
    );
    setCheckedControlIds([]);
    try {
      setDocumentPreviewTitle(rec.policy_procedure_guideline || 'Document Preview');
      const isPlatformAdmin = userRole === 'platform_admin' || userRole === 'admin';
      let shown = false;
      try {
        // Platform admins see the master record's file, other roles their own copy.
        const blob = isPlatformAdmin
          ? await getDocumentLibraryMasterFileBlobApi(recordId)
          : await getCustomerDocumentFileApi(recordId);
        shown = prepareDocumentPreviewFromBlob(blob);
      } catch (err) {
        console.warn('Falling back to the file link:', err);
      }
      if (!shown) {
        setDocumentPreviewDocx(null);
        setDocumentPreviewFile(null);
        const { url } = isPlatformAdmin ? await getDocumentLibraryMasterFileUrlApi(recordId) : await getCustomerDocumentFileUrlApi(recordId);
        await prepareDocumentPreview(url);
      }
      setShowDocumentPreviewModal(true);
    } catch {
      showSnackbar('Could not load file URL.', 'error');
    } finally {
      setViewerLoading(null);
    }
  }
  const handleViewFlaggedFile = async (flaggedId, fileId) => {
    try {
      const res = await getFlaggedFileUrlApi(flaggedId, fileId);
      if (res.success && res.file) {
        window.open(res.file, "_blank");
      }
    } catch (error) {
      console.error("Error fetching flagged file:", error);
    }
  };
  const handleExport = async () => {
    try {
      setExportLoading(true);
      await exportDocumentLibraryMasterApi();
      toast.success('Document library data exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export document library data');
    } finally {
      setExportLoading(false);
    }
  }

const auditorTabsWithAction = ['Ready for audit', 'Flagged by auditor', 'Accepted'];
const showActionColumn =
  userRole === 'platform_admin' || userRole === 'admin' ||
  (userRole === 'auditor' && auditorTabsWithAction.includes(selectedSatsTab));
const showAcknowledgedColumn =
  userRole !== 'platform_admin' && userRole !== 'admin' && userRole !== 'end_user' &&
  !(userRole === 'auditor' && selectedSatsTab === 'Ready for audit');

// Stable per-row identifier for the Auditor "Ready for Audit / Flagged / Accepted"
// rows. The queue endpoints return rows shaped as { _id, controls: {..} | [..] }
// (no top-level `id` or `control_id`), and the Ready-for-Audit pipeline unwinds
// controls so one document produces one row PER control - so the key must
// include the control id, not just the document id. Used by both the
// "Approve/Flag" toggle and the expanded-panel render condition so they always
// agree (the previous code compared `rec._id` on one side and relied on
// `null == undefined` for the control part, which was fragile).
const auditorRowKey = (r) =>
  `${r?._id || r?.id || ''}::${r?.controls?.control_id || r?.controls?.[0]?.control_id || r?.control_id || ''}`;

const handleAuditorAction = async (rec, action) => {
    if (!rec) {
      showSnackbar('This row is missing its document data. Please refresh and try again.', 'error');
      return;
    }
    if (!auditorComment.trim() && action === 'flag') {
      showSnackbar('Please provide comments when flagging a document.', 'error');
      return;
    }
    const documentId = rec.id || rec._id;
    const controlId = rec.control_id || rec.controls?.control_id || rec.controls?.[0]?.control_id || null;
    if (!documentId || !controlId) {
      showSnackbar('Unable to determine which document/control to review. Please refresh and try again.', 'error');
      return;
    }
    setAuditorActionLoading(documentId);
    setAuditorActionType(action);

    try {
      let response;
      if (action === 'approve') {
        response = await approveDocumentApi(documentId, controlId, auditorComment);
      } else {
        response = await flagDocumentApi(documentId, controlId, auditorComment);
        setAuditorComments((prev) => [...prev, { comment: auditorComment, timestamp: new Date().toISOString(), user: user.name }]);
      }

      if (response.success) {
        showSnackbar(`Document ${action === 'approve' ? 'approved' : 'flagged'} successfully.`, 'success');
        setExpandedRow(null);
        setExpandedRowControlId(null)
        setAuditorComment('');
        // Re-fetch the CURRENT auditor queue (and its counts) so the row moves
        // out of "Ready for audit" into "Accepted"/"Flagged". fetchData() would
        // load the default (non-queue) list instead.
        if (userRole === 'auditor' && selectedSatsTab && selectedSatsTab !== 'Total Documents') {
          handleStatClick(selectedSatsTab);
        } else {
          fetchData(currentPage);
        }
      } else {
        throw new Error(response.message || 'Action failed');
      }
    } catch (err) {
      console.error('Auditor action error:', err);
      showSnackbar(err?.response?.data?.error || err.message || 'Failed to submit review.', 'error');
    } finally {
      setAuditorActionLoading(null);
      setAuditorActionType(null);
    }
};

// Comment-only Submit for an already-Accepted/Flagged control - persists the
// comment via addAuditorCommentApi WITHOUT calling approve/flag, so the
// current Accepted/Flagged status is never touched.
const handleAuditorCommentSubmit = async (rec) => {
    if (!rec) {
      showSnackbar('This row is missing its document data. Please refresh and try again.', 'error');
      return;
    }
    if (!auditorComment.trim()) {
      showSnackbar('Please enter a comment before submitting.', 'error');
      return;
    }
    const documentId = rec.id || rec._id;
    const controlId = rec.control_id || rec.controls?.control_id || rec.controls?.[0]?.control_id || null;
    if (!documentId || !controlId) {
      showSnackbar('Unable to determine which document/control to comment on. Please refresh and try again.', 'error');
      return;
    }
    setAuditorActionLoading(documentId);
    setAuditorActionType('comment');

    try {
      const response = await addAuditorCommentApi(documentId, controlId, auditorComment);
      if (response.success) {
        showSnackbar('Comment saved successfully.', 'success');
        setAuditorComment('');
        // Refresh from the backend so the newly-saved comment shows up -
        // status (Accepted/Flagged) is unchanged, so the row stays in the
        // current tab's list.
        if (userRole === 'auditor' && selectedSatsTab && selectedSatsTab !== 'Total Documents') {
          handleStatClick(selectedSatsTab);
        } else {
          fetchData(currentPage);
        }
      } else {
        throw new Error(response.message || 'Failed to save comment');
      }
    } catch (err) {
      console.error('Auditor comment submit error:', err);
      showSnackbar(err?.response?.data?.error || err.message || 'Failed to save comment.', 'error');
    } finally {
      setAuditorActionLoading(null);
      setAuditorActionType(null);
    }
};
useEffect(() => {
  setAuditorComment('');
  setAuditorActionLoading(null);
}, [expandedRow]);

  // Column set mirrors the old getTableHeaders() exactly, now built as
  // common/DataTable columns - meta.filter drives real header filters for
  // Title/Framework/Category (manualFiltering, backed by the same
  // search/frameworkFilter/categoryFilter state fetchData already uses).
  const formatRole = (role) => {
    if (!role) return '';
    return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  const formatAckDate = (dt) => {
    if (!dt) return '';
    try { return new Date(dt).toLocaleDateString(); } catch { return ''; }
  };

  const uniqueControlDomains = [...new Set((records || []).map((rec) => rec.control_domain).filter(Boolean))];
  const uniqueOwners = [...new Set((records || []).map((rec) => rec.owner).filter(Boolean))];

  const columns = [
    {
      id: 'index',
      header: '#',
      enableSorting: false,
      size: 48,
      cell: ({ row }) => (
        <span className="text-gray-500">{(currentPage - 1) * pageSize + row.index + 1}</span>
      ),
    },
    {
      id: 'title',
      accessorFn: (rec) => rec.policy_procedure_guideline,
      header: 'Title',
      enableSorting: false,
      meta: { filter: { variant: 'text', label: 'Title', placeholder: 'Search title' } },
      cell: ({ row }) => (
        <span className="font-medium text-gray-900 min-w-[200px] max-w-[280px] block">
          {row.original.policy_procedure_guideline || <span className="text-gray-400">—</span>}
        </span>
      ),
    },
    {
      id: 'framework',
      header: 'Framework',
      enableSorting: false,
      meta: {
        filter: {
          variant: 'select',
          label: 'Framework',
          placeholder: 'All frameworks',
          options: (frameworks || []).map((fw) => ({ value: fw.id, label: fw.name })),
        },
      },
      cell: ({ row }) => {
        const rec = row.original;
        const ctrlFws = [...new Set(rec.control_frameworks || [])].filter(Boolean);
        const fwNames = ctrlFws.length > 0 ? ctrlFws : (rec.framework_names || []);
        return fwNames.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {fwNames.map((name, i) => (
              <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-gray-700">
                {name}
              </span>
            ))}
          </div>
        ) : <span className="text-gray-400">—</span>;
      },
    },
    {
      id: 'control_domain',
      accessorFn: (rec) => rec.control_domain,
      header: 'Control Domain',
      enableSorting: false,
      meta: {
        filter: {
          variant: 'combobox',
          label: 'Control Domain',
          placeholder: 'All control domains',
          options: uniqueControlDomains.map((v) => ({ value: v, label: v })),
          maxVisible: 4,
        },
      },
      cell: ({ row }) => <span className="text-gray-600">{row.original.control_domain || <span className="text-gray-400">—</span>}</span>,
    },
    {
      id: 'category',
      accessorFn: (rec) => rec.category,
      header: 'Category',
      enableSorting: false,
      meta: {
        filter: {
          variant: 'select',
          label: 'Category',
          placeholder: 'All categories',
          options: allCategories.map((c) => ({ value: c, label: c })),
        },
      },
      cell: ({ row }) => <span className="text-gray-600">{row.original.category || <span className="text-gray-400">—</span>}</span>,
    },
    {
      id: 'owner',
      accessorFn: (rec) => rec.owner,
      header: 'Owner',
      enableSorting: false,
      meta: {
        filter: {
          variant: 'combobox',
          label: 'Owner',
          placeholder: 'All owners',
          options: uniqueOwners.map((v) => ({ value: v, label: v })),
          maxVisible: 4,
        },
      },
      cell: ({ row }) => <span className="text-gray-600">{row.original.owner || <span className="text-gray-400">—</span>}</span>,
    },
    {
      id: 'review_frequency',
      accessorFn: (rec) => rec.review_frequency,
      header: 'Review Frequency',
      enableSorting: false,
      meta: { filter: { variant: 'text', label: 'Review Frequency', placeholder: 'Filter review frequency' } },
      cell: ({ row }) => <span className="text-gray-600">{row.original.review_frequency || <span className="text-gray-400">—</span>}</span>,
    },
  ];

  if (userRole === 'end_user') {
    columns.push({
      id: 'auditor_response',
      header: 'Auditor Response',
      enableSorting: false,
      cell: ({ row }) => {
        const rec = row.original;
        return rec.is_flagged ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600">
            <FlagIcon className="w-3.5 h-3.5" />
            Flagged
          </span>
        ) : rec.is_approved ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600">
            <ShieldCheckIcon className="w-3.5 h-3.5" />
            Approved
          </span>
        ) : <span className="text-gray-400">Pending</span>;
      },
    });
  }

  if (showAcknowledgedColumn) {
    columns.push({
      id: 'acknowledged',
      header: 'Acknowledged',
      enableSorting: false,
      size: 220,
      cell: ({ row }) => {
        const rec = row.original;
        const controls = Array.isArray(rec.controls) ? rec.controls : (rec.controls ? [rec.controls] : []);
        const applicable = controls.filter(c => c.is_applicable !== false);
        const ackKey = auditorRowKey(rec);
        const ackOpen = !!expandedAckRows[ackKey];
        const ackDone = applicable.filter(c => c.is_acknowledged).length;
        const ackTotal = applicable.length;
        if (ackTotal === 0) return <span className="text-xs text-gray-400">—</span>;
        return (
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setExpandedAckRows(prev => ({ ...prev, [ackKey]: !prev[ackKey] }))}
              aria-expanded={ackOpen}
              title={ackOpen ? 'Hide controls' : 'Show controls'}
              className="w-full text-left group"
            >
              <span className="flex items-center gap-1.5">
                {ackDone === ackTotal ? (
                  <CheckCircleIcon className="w-4 h-4 text-green-500 shrink-0" />
                ) : (
                  <span className={`w-3.5 h-3.5 rounded-full border-2 shrink-0 ${ackDone > 0 ? 'border-amber-400' : 'border-gray-300'}`} />
                )}
                <span className="text-xs font-semibold text-gray-800 tabular-nums">{ackDone} / {ackTotal}</span>
                <span className="text-xs text-gray-500">acknowledged</span>
                <ChevronRightIcon className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform group-hover:text-gray-600 ${ackOpen ? 'rotate-90' : ''}`} />
              </span>
              <span className="mt-1.5 block h-1 w-full max-w-[160px] rounded-full bg-gray-100 overflow-hidden">
                <span
                  className={`block h-full rounded-full ${ackDone === ackTotal ? 'bg-green-500' : 'bg-amber-400'}`}
                  style={{ width: `${Math.round((ackDone / ackTotal) * 100)}%` }}
                />
              </span>
            </button>
            {ackOpen && (
              <div className="space-y-1.5 pl-1">
                {applicable.map((ctrl) => (
                  <div key={ctrl.control_id} className="flex items-start gap-1.5">
                    {ctrl.is_acknowledged ? (
                      <CheckCircleIcon className="w-3.5 h-3.5 text-green-500 shrink-0 mt-0.5" />
                    ) : (
                      <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-300 shrink-0 mt-0.5" />
                    )}
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-800 leading-tight">{ctrl.control_code}</p>
                      {ctrl.is_acknowledged ? (
                        <p className="text-xs text-gray-500 leading-tight">
                          by {formatRole(ctrl.acknowledged_user_role)}{ctrl.acknowledgement_at ? ` · ${formatAckDate(ctrl.acknowledgement_at)}` : ''}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-400 leading-tight">Pending</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      },
    });
  }

  columns.push({
    id: 'file',
    header: 'File',
    enableSorting: false,
    cell: ({ row }) => {
      const rec = row.original;
      return rec.file ? (
        <button
          disabled={viewerLoading === rec.id}
          onClick={() => handleViewFile(rec)}
          className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs disabled:opacity-50"
        >
          {viewerLoading === rec.id ? (
            <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>
          ) : (
            <EyeIcon className="w-3.5 h-3.5" />
          )}
          {viewerLoading === rec.id ? 'Loading…' : 'View'}
        </button>
      ) : <span className="text-gray-400">—</span>;
    },
  });

  if (userRole === 'end_user') {
    columns.push({
      id: 'end_user_action',
      header: '',
      enableSorting: false,
      size: 160,
      cell: ({ row }) => {
        const rec = row.original;
        const isOpen = expandedRow === rec.id && expandedRowControlId === rec.control_id && openEndUserAction;
        return (
          <div>
            <button
              onClick={() => {
                setExpandedRow(expandedRow === rec.id ? null : rec.id);
                setExpandedRowControlId(expandedRowControlId === rec.control_id ? null : rec.control_id);
                setOpenEndUserAction(true);
                const extractedComments = (rec.auditor_comments || []).flatMap((resp) =>
                  (resp.comments || []).map((c) => ({
                    comment: c.comment,
                    name: c.comment_by,
                    timestamp: c.comment_on,
                  }))
                );
                setAuditorComments(extractedComments);
              }}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 text-white"
              title="Edit"
            >
              Auditor Feedback
            </button>
            {isOpen && (
              <EndUserFeedbackPanel
                rec={rec}
                handleViewFlaggedFile={handleViewFlaggedFile}
                setShowUploadFlagFile={setShowUploadFlagFile}
                setSelectedFlaggedId={setSelectedFlaggedId}
                newCommentText={newCommentText}
                setNewCommentText={setNewCommentText}
                savingCommentFor={savingCommentFor}
                handleUserComment={handleUserComment}
              />
            )}
          </div>
        );
      },
    });
  }

  if (showActionColumn) {
    columns.push({
      id: 'action',
      header: 'Action',
      enableSorting: false,
      size: 280,
      cell: ({ row }) => {
        const rec = row.original;
        if (userRole === 'auditor' && auditorTabsWithAction.includes(selectedSatsTab)) {
          const k = auditorRowKey(rec);
          const isOpen = expandedRow === k;
          return (
            <div>
              {selectedSatsTab === 'Ready for audit' ? (
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => { setExpandedRow(isOpen ? null : k); setOpenAuditorAction(true); }}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-50 border border-green-200 text-green-700 rounded hover:bg-green-100"
                    title="Review and Approve"
                  >
                    {isOpen ? 'Close' : 'Approve'}
                  </button>
                  <button
                    onClick={() => { setExpandedRow(isOpen ? null : k); setOpenAuditorAction(true); }}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-50 border border-red-200 text-red-700 rounded hover:bg-red-100"
                    title="Review and Flag"
                  >
                    {isOpen ? 'Close' : 'Flag'}
                  </button>
                </div>
              ) : selectedSatsTab === 'Flagged by auditor' ? (
                <div className="flex items-center gap-1.5">
                  <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700">Flagged</span>
                  <button
                    onClick={() => { setExpandedRow(isOpen ? null : k); setOpenAuditorAction(true); }}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-50 border border-blue-200 rounded hover:bg-blue-100"
                    title={isOpen ? 'Close review' : 'Re-review this document'}
                  >
                    {isOpen ? 'Close' : 'Re-review'}
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">Accepted</span>
                  <button
                    onClick={() => { setExpandedRow(isOpen ? null : k); setOpenAuditorAction(true); }}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-gray-50 border border-gray-200 rounded hover:bg-gray-100"
                    title={isOpen ? 'Close' : 'View details'}
                  >
                    {isOpen ? 'Close' : 'View'}
                  </button>
                </div>
              )}
              {isOpen && (
                <AuditorReviewPanel
                  rec={rec}
                  selectedSatsTab={selectedSatsTab}
                  handleViewFlaggedFile={handleViewFlaggedFile}
                  auditorComment={auditorComment}
                  setAuditorComment={setAuditorComment}
                  auditorActionLoading={auditorActionLoading}
                  auditorActionType={auditorActionType}
                  handleAuditorAction={handleAuditorAction}
                  handleAuditorCommentSubmit={handleAuditorCommentSubmit}
                />
              )}
            </div>
          );
        }
        return (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setUploadTarget({ id: rec.id || rec._id, label: rec.policy_procedure_guideline || rec.id || rec._id })}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
              title="Upload File"
            >
              <ArrowDownTrayIcon className="w-3 h-3 rotate-180 text-white" />
              Upload
            </button>
            <button
              onClick={() => setFormModal(rec)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
              title="Edit"
            >
              <PencilSquareIcon className="w-3 h-3 text-white" />
              Edit
            </button>
            {(userRole === 'platform_admin' || userRole === 'admin') && (
              <button
                onClick={() => setDeleteTarget(rec)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100"
                title="Delete"
              >
                <TrashIcon className="w-3 h-3" />
                Delete
              </button>
            )}
          </div>
        );
      },
    });
  }

  const columnFilters = [
    ...(search ? [{ id: 'title', value: search }] : []),
    ...(frameworkFilter ? [{ id: 'framework', value: frameworkFilter }] : []),
    ...(categoryFilter ? [{ id: 'category', value: categoryFilter }] : []),
    ...(controlDomainFilter ? [{ id: 'control_domain', value: controlDomainFilter }] : []),
    ...(ownerFilter ? [{ id: 'owner', value: ownerFilter }] : []),
    ...(reviewFrequencyFilter ? [{ id: 'review_frequency', value: reviewFrequencyFilter }] : []),
  ];

  // Control Domain/Owner/Review Frequency have no dedicated backend filter
  // param (the backend's `search` already covers title/domain/category/
  // owner together as ONE OR'd regex, so three independent values can't
  // all live in that single param at once) - filtered client-side on
  // whatever page is currently loaded, same approach as Incident
  // Reporting's Employee Name filter.
  const displayedRecords = records.filter((rec) => {
    if (controlDomainFilter && !(rec.control_domain || '').toLowerCase().includes(controlDomainFilter.toLowerCase())) return false;
    if (ownerFilter && !(rec.owner || '').toLowerCase().includes(ownerFilter.toLowerCase())) return false;
    if (reviewFrequencyFilter && !(rec.review_frequency || '').toLowerCase().includes(reviewFrequencyFilter.toLowerCase())) return false;
    return true;
  });

  return (
    <>
    <motion.div
      key="dl_master"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.25 }}
      className="space-y-6"
    >
      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
       {stats.map((s) => (
          <div
            key={s.label}
            onClick={() => handleStatClick(s.label)}
            className={`
              bg-white border border-gray-200 rounded-lg p-4 
              flex items-center gap-3 shadow-sm transition-all
              ${userRole === "auditor" ? "cursor-pointer hover:shadow-md" : ""}
              ${selectedSatsTab === s.label ? "border-blue-500 shadow-md" : "border-gray-200"}
            `}
          >
          <div className={`p-2 rounded-lg ${s.color}`}>
              <s.icon className="w-5 h-5" />
          </div>
          <div>
              <p className="text-xs text-gray-500">{s.label}</p>
              <p className="text-xl font-semibold text-gray-900">{s.value}</p>
           </div>
          </div>
      ))}
      </div>

      {/* Header */}
      {(userRole === 'platform_admin' || userRole === 'admin') && (
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Document Library Master</h2>
          <div className="flex items-center gap-3">
            {totalCount > 0 && (
              <span className="text-xs text-gray-500">{totalCount} total records</span>
            )}
            <button
              onClick={handleExport}
              disabled={exportLoading || totalCount === 0}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              {exportLoading ? 'Exporting...' : 'Export'}
            </button>
            <button
              onClick={() => setFormModal({})}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm"
            >
              <PlusIcon className="w-4 h-4" />
              Add New
            </button>
          </div>
        </div>
      )}
      {userRole === 'customer_admin' && (
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Document Library</h2>
          <div className="flex items-center gap-3">
            {totalCount > 0 && (
              <span className="text-xs text-gray-500">{totalCount} total records</span>
            )}
            <button
              onClick={() => setFormModal({})}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm"
            >
              <PlusIcon className="w-4 h-4" />
              Add New Document
            </button>
          </div>
        </div>
      )}
      {/* Table */}
      <DataTable
        columns={columns}
        data={displayedRecords}
        getRowId={(rec, index) => `${rec.id || rec._id || index}-${rec.control_id || rec.controls?.control_id || index}`}
        loading={loading}
        emptyMessage="No document library master records found."
        manualPagination
        rowCount={totalCount}
        pagination={{ pageIndex: (currentPage || 1) - 1, pageSize }}
        onPaginationChange={(updater) => {
          const currentPagination = { pageIndex: (currentPage || 1) - 1, pageSize };
          const nextPagination = typeof updater === 'function' ? updater(currentPagination) : updater;
          if (nextPagination.pageSize !== pageSize) {
            handlePageSizeChange(nextPagination.pageSize);
          } else {
            handlePageChange(nextPagination.pageIndex + 1);
          }
        }}
        enableColumnVisibility={false}
        enableColumnResizing
        enableColumnFilters
        manualFiltering
        columnFilters={columnFilters}
        onColumnFiltersChange={(updater) => {
          const nextFilters = typeof updater === 'function' ? updater(columnFilters) : updater;
          const values = Object.fromEntries(nextFilters.map(({ id, value }) => [id, value]));
          setSearch(values.title || '');
          setFrameworkFilter(values.framework || '');
          setCategoryFilter(values.category || '');
          setControlDomainFilter(values.control_domain || '');
          setOwnerFilter(values.owner || '');
          setReviewFrequencyFilter(values.review_frequency || '');
        }}
        className="overflow-hidden rounded-lg shadow-sm"
        maxBodyHeight="65vh"
      />
    </motion.div>

    {/* Modals rendered outside space-y-6 wrapper to avoid inherited margin-top */}

    {/* Document Viewer Modal */}
    <DocumentPreviewModal
      showDocumentPreviewModal={showDocumentPreviewModal}
      setShowDocumentPreviewModal={setShowDocumentPreviewModal}
      documentPreviewTitle={documentPreviewTitle}
      setDocumentPreviewTitle={setDocumentPreviewTitle}
      documentPreviewLoading={documentPreviewLoading}
      setDocumentPreviewLoading={setDocumentPreviewLoading}
      documentPreviewUrl={documentPreviewUrl}
      setDocumentPreviewUrl={setDocumentPreviewUrl}
      documentPreviewDocx={documentPreviewDocx}
      documentPreviewFile={documentPreviewFile}
      previewScrollContainerRef={previewScrollContainerRef}
      handlePreviewScroll={handlePreviewScroll}
      isPreviewScrollCompleted={isPreviewScrollCompleted}
      setIsPreviewScrollCompleted={setIsPreviewScrollCompleted}
      handleAgreeDocument={handleAgreeDocument}
      previewDocumentMeta={previewDocumentMeta}
      setPreviewDocumentMeta={setPreviewDocumentMeta}
      previewPageCount={previewPageCount}
      setPreviewPageCount={setPreviewPageCount}
      previewCurrentPage={previewCurrentPage}
      setPreviewCurrentPage={setPreviewCurrentPage}
      user={user}
      /* Auditor is a reviewer, not an acknowledger - "I Agree" / acknowledge is
         an employee/customer action only (original Feb-Apr 2026 Auditor role).
         End Users can acknowledge any document visible to them in their
         Document Library, gated by scrolling to the end (see
         DocumentPreviewModal's renderEndOfDocumentAction). */
      showIAgree={
        !isAcknowledged &&
        userRole !== 'platform_admin' && userRole !== 'admin' && userRole !== 'auditor'
      }
      docControls={selectedDocControls}
      showToEmployees={selectedDocShowToEmployees}
      userRole={userRole}
      onConfirmControls={handleConfirmControlsAck}
    />

    {/* Upload File Modal */}
    <UploadFileModal
      showUploadFile={!!uploadTarget}
      setShowUploadFile={(val) => {
        if (!val) {
          setUploadTarget(null);
          setSelectedFile(null);
        }
      }}
      selectedFile={selectedFile}
      setSelectedFile={setSelectedFile}
      handleUploadFile={async (fileArg) => {
        // Accept File, {file}, or [file], or fallback to selectedFile
        let file = fileArg;
        if (!file && selectedFile instanceof File) file = selectedFile;
        if (file && file.file instanceof File) file = file.file;
        if (Array.isArray(file) && file[0] instanceof File) file = file[0];
        if (!(file instanceof File)) {
          showSnackbar('No file selected.', 'error');
          return;
        }
        try {
          if (userRole === 'customer_admin') {
            await uploadCustomerDocumentFileApi(uploadTarget.id, file);
          } else {
            await uploadDocumentLibraryMasterFileApi(uploadTarget.id, file);
          }
          showSnackbar('File uploaded successfully.', 'success');
          setUploadTarget(null);
          setSelectedFile(null);
          fetchData(currentPage);
        } catch (err) {
          showSnackbar(err?.response?.data?.error || 'File upload failed.', 'error');
          throw err;
        }
      }}
      title={uploadTarget ? `Upload File — ${uploadTarget.label}` : 'Upload File'}
    />

    {/* Create / Edit Modal */}
    {formModal && (() => {
      const editId = formModal.id || formModal._id;
      const isCustomerAdmin = userRole === 'customer_admin';
      // Customer Admin rows come from CustomerFrameworkDocument (controls is a
      // list of embedded {control_id, control_code, control_name, ...}), not
      // DocumentLibraryMaster's flat control_ids/control_labels arrays that
      // DocFormModal expects - normalize so already-linked controls show as
      // pre-selected when editing.
      const rawControls = Array.isArray(formModal.controls)
        ? formModal.controls
        : (formModal.controls ? [formModal.controls] : []);
      const editRecord = (isCustomerAdmin && editId)
        ? {
            ...formModal,
            control_ids: rawControls.map((c) => c.control_id).filter(Boolean),
            control_labels: rawControls.map((c) => `${c.control_code || ''} — ${c.control_name || ''}`),
          }
        : formModal;
      return (
        <DocFormModal
          record={editId ? editRecord : null}
          onClose={() => setFormModal(null)}
          {...(isCustomerAdmin ? {
            loadFrameworks: loadCustomerAdminFrameworksForDocForm,
            loadControls: loadCustomerAdminControlsForDocForm,
          } : {})}
          onSave={async (payload) => {
            try {
              if (isCustomerAdmin) {
                if (editId) {
                  await updateCustomerDocumentApi(editId, payload);
                } else {
                  await createCustomerDocumentApi(payload);
                }
              } else if (editId) {
                await updateDocumentLibraryMasterApi(editId, payload);
              } else {
                await createDocumentLibraryMasterApi(payload);
              }
              showSnackbar(editId ? 'Document updated successfully.' : 'Document created successfully.', 'success');
              setFormModal(null);
              fetchData(editId ? currentPage : 1);
            } catch (err) {
              showSnackbar(err?.response?.data?.message || err?.response?.data?.error || 'Operation failed.', 'error');
            }
          }}
        />
      );
    })()}

    {/* Delete Confirm Modal */}
    {deleteTarget && (
      <DeleteConfirmModal
        record={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          try {
            await deleteDocumentLibraryMasterApi(deleteTarget.id);
            showSnackbar('Document deleted successfully.', 'success');
            setDeleteTarget(null);
            fetchData(currentPage);
          } catch (err) {
            showSnackbar(err?.response?.data?.error || 'Delete failed.', 'error');
          }
        }}
      />
    )}
    {showUploadFlagFile && (
      <UploadFileModal
        showUploadFile={showUploadFlagFile}
        setShowUploadFile={setShowUploadFlagFile}
        selectedFile={uploadedFlaggedFile || null}
        setSelectedFile={setUploadedFlaggedFile}
        handleUploadFile={handleUploadFlaggedFile}
        title="Upload Document File"
        subtitle="Upload documents file for completion"
        acceptedFileTypes=".pdf,.doc,.docx,.png,.jpg,.jpeg"
        maxFileSize={2}
        fileTypeLabel={["PDF"]}
      />
    )}
  </>
);
};

export default DocumentLibraryMasterView;

// Extracted panels, rendered inside the "end_user_action"/"action" column
// cells - common/DataTable's body renders exactly one <tr> per row, so
// these can no longer be their own connected full-width sub-row.
const EndUserFeedbackPanel = ({ rec, handleViewFlaggedFile, setShowUploadFlagFile, setSelectedFlaggedId, newCommentText, setNewCommentText, savingCommentFor, handleUserComment }) => (
  <div className="mt-3 w-96 bg-white border rounded-lg p-4 shadow-sm space-y-4">
    <h4 className="text-sm font-semibold text-gray-800">Auditor Feedback</h4>

    <div>
      <h5 className="text-xs font-medium text-gray-700 mb-1">Attached Files</h5>
      {rec.attached_files && rec.attached_files.length > 0 ? (
        <div className="text-xs flex flex-wrap items-center gap-1">
          {rec.attached_files.map((f, idx) => (
            <span key={`file-${idx}`}>
              <a
                onClick={() => handleViewFlaggedFile(rec.flagged_id, f.file_id)}
                className="text-blue-600 hover:underline cursor-pointer"
              >
                {f.file_name}
              </a>
              {idx < rec.attached_files.length - 1 && <span className="text-gray-400">, </span>}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-gray-500 text-xs">No files attached by auditor.</p>
      )}
      {rec.is_flagged && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
          <button
            onClick={() => { setShowUploadFlagFile(true); setSelectedFlaggedId(rec.flagged_id); }}
            className="p-1 text-blue-600 hover:text-blue-900"
            title="Upload file"
          >
            <CloudArrowUpIcon className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>

    <div>
      <h5 className="text-xs font-medium text-gray-700 mb-1">Auditor Comments</h5>
      {rec?.auditor_comments?.length > 0 ? (
        <ul className="max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2 bg-gray-50 text-xs space-y-1">
          {rec.auditor_comments.map((c, idx) => (
            <li key={`comment-${idx}`} className="border-b border-gray-200 last:border-0 pb-1">
              <p className="text-gray-800">{c.comment}</p>
              <p className="text-gray-500 text-xs">By {c.comment_by} on {new Date(c.comment_on).toLocaleString()}</p>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-gray-500 text-xs">No auditor comments.</p>
      )}
    </div>

    {rec.is_flagged && (
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Add a Response</label>
        <textarea
          rows={2}
          className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-blue-500 focus:border-blue-500"
          placeholder="Write your response to the auditor…"
          value={newCommentText}
          onChange={(e) => setNewCommentText(e.target.value)}
          disabled={savingCommentFor === rec.id}
        />
        <div className="flex justify-end mt-2">
          <button
            onClick={() => handleUserComment(rec)}
            disabled={savingCommentFor === rec.id}
            className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
          >
            {savingCommentFor === rec.id ? 'Saving...' : 'Save Comment'}
          </button>
        </div>
      </div>
    )}
  </div>
);

const AuditorReviewPanel = ({ rec, selectedSatsTab, handleViewFlaggedFile, auditorComment, setAuditorComment, auditorActionLoading, auditorActionType, handleAuditorAction, handleAuditorCommentSubmit }) => (
  <div className="mt-3 w-96 bg-white border rounded-lg p-4 shadow-sm space-y-4">
    <h4 className="text-sm font-semibold text-gray-800">Audit Review</h4>

    <div className="grid grid-cols-1 gap-3 text-xs bg-gray-50 p-3 rounded-md">
      <div>
        <span className="text-gray-500">Description:</span>
        <p className="text-gray-800">{rec.control_description_objective || '—'}</p>
      </div>
      <div>
        <span className="text-gray-500">Purpose:</span>
        <p className="text-gray-800">{rec.purpose_coverage || '—'}</p>
      </div>
      <div>
        <span className="text-gray-500">Evidence:</span>
        <p className="text-gray-800">{rec.sample_implementation_evidence || '—'}</p>
      </div>
      <div>
        <span className="text-gray-500">Owner:</span>
        <p className="text-gray-800">{rec.owner || '—'}</p>
      </div>
    </div>

    {selectedSatsTab !== 'Ready for audit' && (
      <>
        <div>
          {(() => {
            const allAttachedFiles = (rec.auditor_responses || []).flatMap(resp => resp.file_details || []);
            const auditorResId = rec.auditor_responses?.[0]?._id;
            return allAttachedFiles.length > 0 && (
              <div className="text-xs flex flex-wrap items-center gap-1">
                {allAttachedFiles.map((f, idx) => (
                  <span key={`file-${idx}`}>
                    <a onClick={() => { handleViewFlaggedFile(auditorResId, f.file_id); }} className="text-blue-600 hover:underline cursor-pointer">
                      {f.file_name}
                    </a>
                    {idx < allAttachedFiles.length - 1 && <span className="text-gray-400">, </span>}
                  </span>
                ))}
              </div>
            );
          })()}
        </div>
        <div>
          <h5 className="text-xs font-medium text-gray-700 mb-1">Auditor Comments</h5>
          {(() => {
            const embeddedComments = rec.controls?.auditor_comments || [];
            const allComments = embeddedComments.length > 0
              ? embeddedComments
              : (rec.auditor_responses || []).flatMap(resp => resp.comments || []);
            return allComments.length > 0 ? (
              <ul className="max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2 bg-gray-50 text-xs space-y-1">
                {allComments.map((c, idx) => (
                  <li key={`comment-${idx}`} className="border-b border-gray-200 last:border-0 pb-1">
                    <p className="text-gray-800">{c.comment}</p>
                    <p className="text-gray-500 text-xs">By {c.comment_by} on {new Date(c.comment_on).toLocaleString()}</p>
                  </li>
                ))}
              </ul>
            ) : <p className="text-gray-500 text-xs">No auditor comments.</p>;
          })()}
        </div>
      </>
    )}

    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">Auditor Comments</label>
      <textarea
        rows={3}
        className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-blue-500 focus:border-blue-500"
        placeholder="Add your comments here…"
        value={auditorComment}
        onChange={(e) => setAuditorComment(e.target.value)}
        disabled={auditorActionLoading}
      />
    </div>

    {(selectedSatsTab === 'Accepted' || selectedSatsTab === 'Flagged by auditor') && (
      <div className="flex gap-3 pt-2">
        <button
          onClick={() => handleAuditorCommentSubmit(rec)}
          disabled={auditorActionLoading}
          className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
        >
          {(auditorActionLoading === (rec.id || rec._id)) && auditorActionType === 'comment' ? (
            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>
          ) : null}
          Submit
        </button>
      </div>
    )}

    {selectedSatsTab !== 'Accepted' && (
      <div className="flex gap-3 pt-2">
        <button
          onClick={() => handleAuditorAction(rec, 'approve')}
          disabled={auditorActionLoading}
          className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 inline-flex items-center gap-2"
        >
          {auditorActionLoading === rec.id && auditorActionType === 'approve' ? (
            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>
          ) : null}
          Approve
        </button>
        <button
          onClick={() => handleAuditorAction(rec, 'flag')}
          disabled={auditorActionLoading}
          className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
        >
          {auditorActionLoading === rec.id && auditorActionType === 'flag' ? (
            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>
          ) : null}
          {selectedSatsTab === 'Flagged by auditor' ? 'Flag Again' : 'Flag'}
        </button>
      </div>
    )}
  </div>
);
